<template>
  <app-modal
    v-model="isNewsletterModalActive"
    :title="isSubscribed ? $t('newsletter_pending_title') : $t('general_receive_newsletter')"
    body-class="space-y-3"
    :confirm-label="isSubscribed ? '' : $t('general_button_signup_newsletter')"
    :confirm-loading="isLoading"
    :confirm-disabled="!contactForm.email || !contactForm.consent"
    :cancel-label="isSubscribed ? $t('general_button_close') : ''"
    :close-on-confirm="false"
    show-cancel
    @cancel="closeModal"
    @confirm="confirmModal"
  >
    <template v-if="isSubscribed">
      <p>{{ $t('newsletter_pending_body') }}</p>
      <!-- ponytail: one link, always external. `newsUrl` is whatever the API
           says the mail links to, so there is no second copy of the URL here
           and no in-app-vs-archive branch to keep in sync. -->
      <text-link
        :href="newsUrl"
        target="_blank"
        :label="$t('newsletter_read_latest_news')"
        :icon="mdiOpenInNew"
      />
    </template>
    <template v-else>
      <p>{{ $t('newsletter_introduction') }}</p>
      <app-input
        name="email"
        type="email"
        autocomplete="email"
        required
        :label="$t('general_email')"
        :help="$t('general_email_help_required')"
        :error="errorEmail"
        v-model="contactForm.email"
      />
      <app-checkbox v-model="contactForm.consent" name="newsletter-consent" required :label="$t('newsletter_consent')" />
    </template>
    <template #footer-start>
      <text-link to="/contact" :label="$t('general_button_ask_for_help')" :icon="mdiLifebuoy" class="text-sm" />
    </template>
  </app-modal>
</template>

<script>
import { useNotification } from '@/composables/useNotification'
import { computed, reactive, ref } from 'vue'
import { mdiLifebuoy, mdiOpenInNew } from '@mdi/js'
import AppCheckbox from '@/components/ui/forms/AppCheckbox.vue'
import AppInput from '@/components/ui/forms/AppInput.vue'
import AppModal from '@/components/ui/overlays/AppModal.vue'
import TextLink from '@/components/ui/layout/TextLink'
import EmailRepository from '@/repositories/EmailRepository'
import { useI18n } from 'vue-i18n'
import { useDisplayStore } from '@/stores/useDisplayStore';
import { useAuthStore } from '@/stores/useAuthStore';

export default {
  name: 'ModalNewsletter',
  components: {
    AppCheckbox,
    AppInput,
    AppModal,
    TextLink
  },
  setup () {
    const { t, locale } = useI18n()
    const authStore = useAuthStore();
    const displayStore = useDisplayStore();
    const { notifyError } = useNotification()
    const isLoading = ref(false)
    const isSubscribed = ref(false)
    const newsUrl = ref('')

    const contactForm = reactive({
      email: authStore.userEmail,
      consent: false
    })

    const errorEmail = ref('')

    // Computed value
    const isNewsletterModalActive = computed({
      get: () => displayStore.isNewsletterModalActive,
      set: value => displayStore.newsletterModalToggle(value)
    })

    // Close modal through user action. Reset so reopening starts on the form
    // rather than on a stale confirmation.
    const closeModal = () => {
      displayStore.newsletterModalToggle(false)
      isSubscribed.value = false
      contactForm.consent = false
      errorEmail.value = ''
    }

    // FastAPI answers a validation failure with a LIST of {loc, msg} objects;
    // rendering that straight into a toast produced "[object Object]".
    const errorMessage = (error) => {
      const detail = error?.response?.data?.detail
      if (Array.isArray(detail)) return detail[0]?.msg || t('messages.server_error')
      return detail || t('messages.server_error')
    }

    // The wording behind the checkbox is `newsletter_consent`; change it and this version together.
    const CONSENT_TEXT_VERSION = 'portal-2026-11'

    const confirmModal = () => {
      if (isLoading.value || !contactForm.consent) return
      isLoading.value = true
      errorEmail.value = ''

      EmailRepository.subscribeNewsletter({
        email: contactForm.email,
        consent: contactForm.consent,
        consentTextVersion: CONSENT_TEXT_VERSION,
        requestId: crypto.randomUUID(),
        locale: locale?.value
      }).then(response => {
        // Stay open and switch to the confirmation panel — the modal used to
        // close on click (AppModal's `closeOnConfirm` default), so the success
        // toast landed half a second later with no dialog to belong to (PI-166).
        newsUrl.value = response.data?.news_url || ''
        isSubscribed.value = true
      }).catch((error) => {
        // Keep the modal open on failure, so the address is still there to fix.
        errorEmail.value = errorMessage(error)
        notifyError(errorEmail.value)
      }).finally(() => {
        isLoading.value = false
      })
    }

    return {
      closeModal,
      contactForm,
      confirmModal,
      errorEmail,
      isLoading,
      isNewsletterModalActive,
      isSubscribed,
      newsUrl,
      mdiOpenInNew,
      mdiLifebuoy
    }
  }
}
</script>
